/*
 * Copyright (c) 2025-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, computed, HostListener, OnInit} from '@angular/core';
import {KtsComponent} from '../kts/kts.component';
import {KtsTimingTemplateRow} from '../../model/ktsTimingTemplateRow.model';
import {ColDef} from 'ag-grid-community';
import {CommonHelperService} from '../../service/common-helper.service';
import {ProgramRun} from '../../model/programRun';
@Component({
    selector: 'app-kts-admin',
    templateUrl: './kts-admin.component.html',
    styleUrls: ['./kts-admin.component.scss'],
    standalone: false
})
export class KtsAdminComponent extends KtsComponent implements OnInit {
  readonly versionDropdownOptions = computed(() => {
    const currentType = this.typeSignal();
    this.ktsTimingTemplateRowMapSignal();
    const fullVersions = Array.from(this.fullCols.get(currentType)?.keys() || []);
    if (fullVersions.length === 0) {
      return [] as { label: string; value: string }[];
    }
    fullVersions.sort((a, b) => a.localeCompare(b));
    return fullVersions
      .filter((fullVersion) => {
        const status = this.getStatusOfVersion(fullVersion, currentType);
        if (currentType === 'ECS' && !KtsTimingTemplateRow.isAllowedEcsTemplateVersion(fullVersion)) {
          return false;
        }
        return status === 'ACTIVE' || status === 'DRAFT';
      })
      .map((fullVersion) => {
        return {
          label: `${KtsTimingTemplateRow.getMajorVersionFromFullVersion(fullVersion)} ${this.getStatusOfVersionForLabel(fullVersion, currentType)}`.trim(),
          value: fullVersion
        };
      });
  });
  allowDuplicates = true;
  permissionUrl = '/esciUI/ktsEditorPermission';
  templateUrl = '/esciUI/ktsTimingTemplatesForEditor';
  primaryProgramCount = 0;

  // ICDe Primary Timing Report Selection Modal
  showTimingSelectionModal: boolean;
  programRuns = new Map<string, Array<ProgramRun>>();
  columnDefs: ColDef[] = [
    { field: 'primaryProgram', headerName: 'Primary', maxWidth: 100, editable: true},
    { field: 'programName', headerName: 'Program Name', maxWidth: 400 },
    { field: 'templateVersion', headerName: 'Template Version', maxWidth: 400 },
    {
      field: 'archived',
      headerName: 'Archived',
      maxWidth: 120,
      valueFormatter: (params) => params.data?.archived ? 'Yes' : 'No'
    },
    {
      field: 'createdDate',
      headerName: 'Created Date',
      maxWidth: 400,
      valueFormatter: (params) => CommonHelperService.formatDate(params.data?.createdDate),
      comparator: (valueA, valueB, nodeA, nodeB) => {
        const timeA = CommonHelperService.toEpochMillis(nodeA?.data?.createdDate);
        const timeB = CommonHelperService.toEpochMillis(nodeB?.data?.createdDate);
        return timeA - timeB;
      }
    },
    { field: 'createdBy', headerName: 'Created By', maxWidth: 400 },
  ];

  ngOnInit(): void {
    this.esciService.buttonClick$.subscribe(() => {
      this.doTimingCalculations();
    });
    this.permission.set(false);
    this.esciService.getPermissionAndFileSizeLimit(this, false);
    this.esciService.getKtsTimingTemplates(this, true);
    this.loadProgramsForType();
    this.programRuns = this.esciService.getProgramRuns(this, null, false, true);
    this.refreshPrimaryProgramCount();
  }

  override onTemplateTypeChange(): void {
    this.showTimingSelectionModal = false;
    super.onTemplateTypeChange();
  }

  override clearPrograms(): void {
    this.showTimingSelectionModal = false;
    super.clearPrograms();
    this.setStudyModeBooleansAndResetAdjustments();
  }

  isIcdePrimaryLoading(): boolean {
    return this.loadingMomentumDataSignal();
  }

  isIcdePrimaryDisabled(): boolean {
    return (this.programRuns.get('PROD') ?? []).length === 0;
  }

  override afterProgramRunsLoaded(updatedProgramRuns: Map<string, ProgramRun[]>): void {
    super.afterProgramRunsLoaded(updatedProgramRuns);
    // getProgramRuns calls this for every lookup, including the filtered overwrite/compare
    // requests inherited from KtsComponent. Only an unfiltered Admin reload assigns that
    // map onto programRuns; a scoped result must leave the primary-timing list intact.
    if (updatedProgramRuns !== this.programRuns) {
      return;
    }
    this.programRuns = new Map(updatedProgramRuns);
    this.loading = false;
    this.setLoadingMomentumData(false);
    this.refreshPrimaryProgramCount();
  }

  isVersionOverrideDisabled(): boolean {
    if (this.loadingMomentumDataSignal() || !this.ktsTimingTemplateRowMapSignal()?.size) {
      return true;
    }
    if (this.type === 'ECS') {
      return false;
    }
    return !this.selectedModelYear || !this.momentumProgramMap.get(this.selectedModelYear.year)?.length;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event?: any) {
    const api = this.gridApiMap?.get(this.version);
    if (api && !api.isDestroyed()) {
      api.sizeColumnsToFit();
    }
  }

  toggleStudyMode() {
    let message = `Are you sure you want to turn on Study Mode? Only Milestones will be adjustable and no timings can be saved once you enter Study Mode. All previous adjustments will be removed.`;
    if (this.studyMode) {
      message = `Are you sure you want to turn off Study Mode? This will clear out all date adjustments. Please ensure you've downloaded any timing files you need.`;
    }
    this.confirmationService.confirm({
      message: message,
      header: 'Study Mode',
      key: 'studyModeConfirmation',
      icon: 'pi pi-exclamation-circle',
      acceptButtonStyleClass: 'p-button-text',
      rejectButtonStyleClass: 'p-button-text',
      acceptIcon: 'pi pi-check mr-2',
      rejectIcon: 'pi pi-times mr-2',
      accept: () => {
        this.studyMode = !this.studyMode;
        this.setStudyModeBooleansAndResetAdjustments();
      }
    });
  }

  public setStudyModeBooleansAndResetAdjustments() {
    const typeMap = this.ktsTimingTemplateRowMap.get(this.type);
    if (!typeMap) {
      return;
    }
    typeMap.forEach((rows) => {
      rows.forEach(row => {
        row.studyMode = this.studyMode;
        row.programData.forEach(program => {
          program.adjustedWeeks = 0;
        });
      });
    });
    this.doTimingCalculations();
    this.gridApiMap.forEach((api) => {
        api.redrawRows();
    });
  }

  savePrimaryTimingPrograms() {
    this.showTimingSelectionModal = false;
    this.setLoadingMomentumData(true);
    this.esciService.savePrimaryTimingPrograms(this, this.programRuns.get('PROD'), () => {
      this.refreshProdProgramRuns();
    });
  }

  protected override afterProgramTimingSaved(forAdmin = false): void {
    if (forAdmin) {
      this.refreshProdProgramRuns();
    }
  }

  private refreshProdProgramRuns(): void {
    this.programRuns = this.esciService.getProgramRuns(this, null, false, true);
  }

  refreshPrimaryProgramCount() {
    this.primaryProgramCount = (this.programRuns.get('PROD') ?? []).filter(a => a.primaryProgram).length;
  }

  onPrimaryProgramCellValueChanged() {
    this.refreshPrimaryProgramCount();
  }

  resetPrimaryPrograms() {
    this.showTimingSelectionModal = false;
    this.loading = true;
    this.programRuns = this.esciService.getProgramRuns(this, null, false, true);
  }

  openPrimaryTimingModal() {
    this.showTimingSelectionModal = true;
    this.refreshPrimaryProgramCount();
    this.onResize();
  }
}
